<script lang="ts">
  import { pmStore, hydrateBoards } from '../stores/pm.svelte.ts';
  import { agentStore } from '../stores/agents.svelte.ts';
  import type { BoardDetail, Project } from '../types.ts';

  interface Props {
    onOpenCard?: (id: string) => void;
    onOpenBoard?: (projectId: string) => void;
  }
  let { onOpenBoard }: Props = $props();

  interface ProjectHealth {
    project: Project;
    boardDetail: BoardDetail | null;
    totalCards: number;
    todoCards: number;
    inProgressCards: number;
    doneCards: number;
    urgentCards: number;
    overdueCards: number;
    runningAgents: number;
    health: 'green' | 'yellow' | 'red' | 'none';
  }

  const projectHealthList: ProjectHealth[] = $derived((() => {
    return pmStore.projects.map((project) => {
      const boardDetail = Object.values(pmStore.boardDetails).find((detail) => detail.project_id === project.id) ?? null;
      const columns = boardDetail?.columns ?? [];
      const allCards = columns.flatMap((column) => column.cards);
      const totalCards = allCards.length;

      let todoCards = 0;
      let inProgressCards = 0;
      let doneCards = 0;

      if (columns.length === 1) {
        todoCards = columns[0].cards.length;
      } else if (columns.length > 1) {
        const todoIndex = columns.findIndex((column) => {
          const name = column.name.toLowerCase();
          return name === 'backlog' || name === 'todo';
        });
        const doneIndex = columns.findIndex((column) => column.name.toLowerCase() === 'done');
        const resolvedTodoIndex = todoIndex >= 0 ? todoIndex : 0;
        const resolvedDoneIndex = doneIndex >= 0 ? doneIndex : columns.length - 1;

        columns.forEach((column, index) => {
          if (index === resolvedTodoIndex) {
            todoCards += column.cards.length;
          } else if (index === resolvedDoneIndex) {
            doneCards += column.cards.length;
          } else {
            inProgressCards += column.cards.length;
          }
        });
      }

      const urgentCards = allCards.filter((card) => card.priority === 'urgent').length;
      const overdueCards = allCards.filter((card) => card.due_date && new Date(card.due_date) < new Date()).length;
      const runningAgents = allCards.length === 0
        ? 0
        : agentStore.running.filter((running) => allCards.some((card) => card.agent_id === running.agent_id)).length;

      let health: ProjectHealth['health'] = 'none';
      if (totalCards > 0) {
        if (urgentCards > 3 || overdueCards > 2) health = 'red';
        else if (urgentCards > 0 || overdueCards > 0) health = 'yellow';
        else health = 'green';
      }

      return {
        project,
        boardDetail,
        totalCards,
        todoCards,
        inProgressCards,
        doneCards,
        urgentCards,
        overdueCards,
        runningAgents,
        health,
      };
    });
  })());

  const HEALTH_COLORS = {
    green: 'oklch(0.72 0.18 145)',
    yellow: 'oklch(0.82 0.18 75)',
    red: 'oklch(0.62 0.22 25)',
    none: 'oklch(0.45 0.04 310 / 0.45)',
  } as const;

  const HEALTH_LABELS = {
    green: 'On track',
    yellow: 'At risk',
    red: 'Off track',
    none: 'No data',
  } as const;

  function progressWidth(count: number, total: number): number {
    return total > 0 ? Math.round((count / total) * 100) : 0;
  }

  function columnFillColor(color: string | null | undefined): string {
    return color && color.trim().startsWith('oklch(')
      ? color
      : 'oklch(0.40 0.04 310 / 0.35)';
  }

  async function openProject(projectId: string): Promise<void> {
    await hydrateBoards(projectId);
    pmStore.activeProjectId = projectId;
  }

  async function openBoard(e: MouseEvent, project: Project): Promise<void> {
    e.stopPropagation();
    await hydrateBoards(project.id);
    pmStore.activeProjectId = project.id;
    onOpenBoard?.(project.id);
  }

  function handleProjectKeydown(e: KeyboardEvent, projectId: string): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void openProject(projectId);
    }
  }
</script>

<div class="portfolio-view">
  <header class="view-header">
    <h2>Portfolio</h2>
    <span class="sub">All projects at a glance</span>
  </header>

  {#if pmStore.projects.length === 0}
    <div class="empty-state">No projects yet. Create a project to see its portfolio status.</div>
  {:else}
    <div class="portfolio-grid">
      {#each projectHealthList as ph (ph.project.id)}
        <div
          class="project-card"
          role="button"
          tabindex="0"
          onclick={() => void openProject(ph.project.id)}
          onkeydown={(e) => handleProjectKeydown(e, ph.project.id)}
        >
          <div class="pc-header">
            <span
              class="health-dot"
              style="background: {HEALTH_COLORS[ph.health]}; box-shadow: 0 0 8px {HEALTH_COLORS[ph.health]}"
              aria-hidden="true"
            ></span>
            <span class="pc-name">{ph.project.name}</span>
            {#if ph.runningAgents > 0}
              <span class="running-chip">{ph.runningAgents} running</span>
            {:else}
              <span class="health-label" style="color: {HEALTH_COLORS[ph.health]}">{HEALTH_LABELS[ph.health]}</span>
            {/if}
          </div>

          {#if ph.totalCards > 0}
            <div class="workload-bar" title="Todo: {ph.todoCards}  In progress: {ph.inProgressCards}  Done: {ph.doneCards}">
              {#if ph.todoCards > 0}
                <div class="wb-seg wb-todo" style="width: {progressWidth(ph.todoCards, ph.totalCards)}%"></div>
              {/if}
              {#if ph.inProgressCards > 0}
                <div class="wb-seg wb-inprogress" style="width: {progressWidth(ph.inProgressCards, ph.totalCards)}%"></div>
              {/if}
              {#if ph.doneCards > 0}
                <div class="wb-seg wb-done" style="width: {progressWidth(ph.doneCards, ph.totalCards)}%"></div>
              {/if}
            </div>
          {:else}
            <div class="workload-bar empty-bar"></div>
          {/if}

          <div class="pc-stats">
            <span class="stat"><span class="stat-num">{ph.totalCards}</span> cards</span>
            <span class="stat done-stat"><span class="stat-num">{ph.doneCards}</span> done</span>
            {#if ph.urgentCards > 0}
              <span class="stat urgent-stat"><span class="stat-num">{ph.urgentCards}</span> urgent</span>
            {/if}
            {#if ph.overdueCards > 0}
              <span class="stat overdue-stat"><span class="stat-num">{ph.overdueCards}</span> overdue</span>
            {/if}
          </div>

          {#if ph.boardDetail}
            <div class="col-breakdown">
              {#each ph.boardDetail.columns as col}
                <div class="col-bar" title="{col.name}: {col.cards.length} cards">
                  <div
                    class="col-fill"
                    style="height: {Math.min(100, col.cards.length * 16)}px; background: {columnFillColor(col.color)}"
                  ></div>
                  <span class="col-count">{col.cards.length}</span>
                  <span class="col-name">{col.name}</span>
                </div>
              {/each}
            </div>
          {:else}
            <div class="no-board">Click to load board data</div>
          {/if}

          <button
            class="open-board-btn"
            onclick={(e) => void openBoard(e, ph.project)}
            title="Open board"
          >
            Open board →
          </button>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .portfolio-view {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem 1.75rem;
  }
  .portfolio-view::-webkit-scrollbar {
    width: 6px;
  }
  .portfolio-view::-webkit-scrollbar-thumb {
    background: oklch(0.30 0.05 275 / 0.35);
    border-radius: 3px;
  }
  .view-header {
    display: flex;
    align-items: baseline;
    gap: 0.625rem;
    margin-bottom: 1.25rem;
  }
  .view-header h2 {
    margin: 0;
    font-size: 1.125rem;
    color: oklch(0.92 0.06 310);
    font-weight: 600;
  }
  .sub {
    font-size: 0.75rem;
    color: oklch(0.45 0.04 310 / 0.55);
  }
  .empty-state {
    color: oklch(0.45 0.04 310 / 0.55);
    font-size: 0.875rem;
    padding: 3rem;
    text-align: center;
  }
  .portfolio-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 0.875rem;
  }
  .project-card {
    background: oklch(0.11 0.018 275 / 0.70);
    border: 1px solid oklch(0.30 0.05 275 / 0.22);
    border-radius: 10px;
    padding: 1rem;
    cursor: pointer;
    transition: border-color 150ms ease, background 150ms ease;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    outline: none;
  }
  .project-card:hover,
  .project-card:focus-visible {
    border-color: oklch(0.72 0.28 310 / 0.20);
    background: oklch(0.13 0.02 275 / 0.80);
  }
  .pc-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .health-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .pc-name {
    flex: 1;
    font-size: 0.875rem;
    color: oklch(0.88 0.06 310);
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .health-label {
    font-size: 0.62rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 600;
    flex-shrink: 0;
  }
  .running-chip {
    font-size: 0.60rem;
    font-weight: 700;
    padding: 0.1rem 0.45rem;
    border-radius: 10px;
    background: oklch(0.72 0.18 145 / 0.14);
    color: oklch(0.72 0.18 145);
    border: 1px solid oklch(0.72 0.18 145 / 0.28);
    font-family: 'JetBrains Mono', monospace;
    animation: run-pulse 1.5s ease-in-out infinite;
    flex-shrink: 0;
  }
  @keyframes run-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.60; }
  }
  .workload-bar {
    height: 5px;
    border-radius: 3px;
    background: oklch(0.22 0.03 275 / 0.50);
    overflow: hidden;
    display: flex;
  }
  .workload-bar.empty-bar {
    background: oklch(0.20 0.03 275 / 0.30);
  }
  .wb-seg {
    height: 100%;
    transition: width 400ms ease;
  }
  .wb-todo {
    background: oklch(0.45 0.04 310 / 0.40);
  }
  .wb-inprogress {
    background: oklch(0.72 0.28 310 / 0.80);
  }
  .wb-done {
    background: oklch(0.72 0.18 145 / 0.80);
  }
  .pc-stats {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .stat {
    font-size: 0.68rem;
    color: oklch(0.55 0.04 310 / 0.65);
  }
  .stat-num {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.72rem;
    font-weight: 600;
    color: oklch(0.75 0.06 310 / 0.85);
    margin-right: 0.1rem;
  }
  .done-stat .stat-num {
    color: oklch(0.72 0.18 145 / 0.85);
  }
  .urgent-stat .stat-num {
    color: oklch(0.72 0.22 25 / 0.90);
  }
  .overdue-stat .stat-num {
    color: oklch(0.62 0.22 25 / 0.90);
  }
  .col-breakdown {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
    min-height: 40px;
  }
  .col-bar {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    flex: 1;
  }
  .col-fill {
    width: 100%;
    min-height: 4px;
    border-radius: 3px 3px 0 0;
    transition: height 300ms ease;
  }
  .col-count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.60rem;
    color: oklch(0.55 0.04 310 / 0.70);
  }
  .col-name {
    font-size: 0.56rem;
    color: oklch(0.45 0.04 310 / 0.55);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  .no-board {
    font-size: 0.70rem;
    color: oklch(0.40 0.03 310 / 0.45);
    font-style: italic;
    text-align: center;
    padding: 0.5rem 0;
  }
  .open-board-btn {
    background: transparent;
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    color: oklch(0.55 0.12 265 / 0.75);
    font-size: 0.70rem;
    padding: 0.3rem 0.7rem;
    border-radius: 5px;
    cursor: pointer;
    width: 100%;
    text-align: center;
    font-family: inherit;
    transition: color 100ms ease, border-color 100ms ease, background 100ms ease;
    margin-top: 0.1rem;
  }
  .open-board-btn:hover {
    color: oklch(0.75 0.18 265 / 0.95);
    border-color: oklch(0.72 0.28 310 / 0.25);
    background: oklch(0.12 0.02 275 / 0.50);
  }
  @media (prefers-reduced-motion: reduce) {
    .running-chip {
      animation: none;
    }
  }
</style>
